import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, Empty, useIsClient, statusLabel } from "@/components/common";
import RecycleBin from "./RecycleBin";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RequestDialog from "./RequestDialog";
import { CategoryCombobox } from "./CategoryCombobox";
import { ImportExcel } from "./ImportExcel";
import { StatTiles, PhaseCard, StatusChart, DueChart, TeamCard, ActivityCard, RequestsPanel } from "./DashboardParts";
import { RollForwardDialog } from "./RollForwardDialog";
import { EngagementDeleteDialog } from "./EngagementLifecycle";
import { AddTeamDialog } from "./AddTeamDialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Clock, AlertTriangle, CalendarClock, Users, UserPlus,
  Activity, LayoutGrid, List, Loader2, Trash2, Lock, MoreVertical, Archive,
  ArchiveRestore, History, Inbox, Download, Paperclip,
} from "lucide-react";

const STATUSES = ["Received", "Awaiting Client", "Submitted", "Complete"];

export default function EngagementWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { session } = useAuth();
  const [eng, setEng] = useState(null);
  const [requests, setRequests] = useState([]);
  const [cats, setCats] = useState([]);
  const [activity, setActivity] = useState([]);
  const [filters, setFilters] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [presetCat, setPresetCat] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addSide, setAddSide] = useState(null);
  const [openReq, setOpenReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rollOpen, setRollOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [deleteReqOpen, setDeleteReqOpen] = useState(false);
  const [receivedOpen, setReceivedOpen] = useState(false);

  const canManage = session?.roles?.some((r) => ["partner", "manager", "tenant_admin"].includes(r));
  const isClient = useIsClient();
  const canInvite = session?.roles?.some((r) => ["partner", "manager", "tenant_admin", "client_admin"].includes(r));
  const isArchived = eng?.status === "archived";
  const readOnly = isArchived || eng?.status === "deleted";

  const load = useCallback(async () => {
    try {
      const [e, r, c, a] = await Promise.all([
        api.get(`/m1/engagements/${id}`),
        api.get(`/m1/requests?engagement_id=${id}${showDeleted ? "&only_deleted=true" : ""}`),
        api.get("/m1/categories").catch(() => ({ data: [] })),
        api.get(`/activity?engagement_id=${id}`).catch(() => ({ data: [] })),
      ]);
      setEng(e.data); setRequests(r.data); setCats(c.data); setActivity(a.data);
    } catch (er) { toast.error(apiError(er)); } finally { setLoading(false); }
  }, [id, showDeleted]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedIds([]); }, [filters, showDeleted]);

  // auto-open roll-forward when arriving from "roll forward from previous" create flow
  useEffect(() => {
    if (!loading && eng && sp.get("rollforward") === "1" && canManage && !readOnly) {
      setRollOpen(true);
      sp.delete("rollforward"); setSp(sp, { replace: true });
    }
  }, [loading, eng]); // eslint-disable-line

  const archiveAct = async (path, label) => {
    try { await api.post(`/m1/engagements/${id}/${path}`); toast.success(label); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitAllToClient = async () => {
    try {
      const r = await api.post(`/m1/engagements/${id}/submit-to-client`);
      toast.success(`Released to client — ${r.data.submitted} request(s) now Awaiting Client, ${r.data.notified} client user(s) notified`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runBulk = async (payload, msg) => {
    try {
      const r = await api.post(`/m1/engagements/${id}/bulk-action`, { request_ids: selectedIds, ...payload });
      toast.success(msg(r.data));
      setSelecting(false); setSelectedIds([]); setReassignOpen(false); setDeleteReqOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeMember = async (m) => {
    try {
      await api.post(`/m1/engagements/${id}/remove-member/${m.id}?side=${m.external ? "client" : "internal"}`);
      toast.success(`${m.name} removed from this engagement`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const today0 = new Date(new Date().toDateString());
  const endWeek = new Date(today0); endWeek.setDate(today0.getDate() + 7);
  const endNext = new Date(today0); endNext.setDate(today0.getDate() + 14);
  const isOverdue = (r) => r.due_date && r.status !== "Complete" && new Date(r.due_date) < today0;
  const dueBucketOf = (r) => {
    if (!r.due_date || r.status === "Complete") return null;
    const d = new Date(r.due_date);
    if (d < today0) return "overdue";
    if (d <= endWeek) return "week";
    if (d <= endNext) return "next";
    return "later";
  };
  const assignedTo = (r, uid) => [...(r.assigned_internal || []), ...(r.assigned_client || [])].includes(uid);
  const passes = (r, f) => {
    switch (f.type) {
      case "status": return r.status === f.value;
      case "overdue": return isOverdue(r);
      case "complete": return r.status === "Complete";
      case "dueThisWeek": return dueBucketOf(r) === "week";
      case "upcoming": { const b = dueBucketOf(r); return b === "next" || b === "later"; }
      case "phase": return r.phase_id === f.value;
      case "assignee": return assignedTo(r, f.value);
      case "due": return dueBucketOf(r) === f.value;
      case "mine": return assignedTo(r, session?.user_id);
      default: return true;
    }
  };
  const filtered = useMemo(() => requests.filter((r) => filters.every((f) => passes(r, f))), [requests, filters]); // eslint-disable-line
  const toggleFilter = (type, value, label) => {
    setFilters((prev) => {
      const hit = prev.find((x) => x.type === type && x.value === value);
      if (hit) return prev.filter((x) => !(x.type === type && x.value === value));
      const rest = prev.filter((x) => x.type !== type);
      return [...rest, { id: value ? `${type}-${value}` : type, type, value, label }];
    });
  };
  const activeType = (type, value) => filters.some((f) => f.type === type && (value === undefined || f.value === value));

  const statusCounts = { Received: 0, "Awaiting Client": 0, Submitted: 0, Complete: 0, Overdue: 0 };
  const dueCounts = { overdue: 0, week: 0, next: 0, later: 0 };
  requests.forEach((r) => {
    statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
    if (isOverdue(r)) statusCounts.Overdue++;
    const b = dueBucketOf(r); if (b) dueCounts[b]++;
  });
  const total = requests.length;
  const toggleSelect = (rid) => setSelectedIds((prev) => prev.includes(rid) ? prev.filter((x) => x !== rid) : [...prev, rid]);
  const toggleCategory = (ids) => setSelectedIds((prev) => ids.every((i) => prev.includes(i)) ? prev.filter((i) => !ids.includes(i)) : [...new Set([...prev, ...ids])]);
  const selectAllVisible = () => { const all = filtered.map((r) => r.id); setSelectedIds((prev) => all.every((i) => prev.includes(i)) ? prev.filter((i) => !all.includes(i)) : all); };
  const completeCount = statusCounts.Complete;
  const percent = total ? Math.round((completeCount / total) * 100) : 0;
  const dueCats = new Set(requests.filter((r) => dueBucketOf(r) === "week").map((r) => r.category_name)).size;
  const phaseProgress = (eng?.phases || []).map((ph) => {
    const rs = requests.filter((r) => r.phase_id === ph.id);
    const done = rs.filter((r) => r.status === "Complete").length;
    return { id: ph.id, name: ph.name, pct: rs.length ? Math.round((done / rs.length) * 100) : 0 };
  });
  const members = [
    ...(eng?.internal_roster || []).map((u) => ({ ...u, external: false })),
    ...(eng?.client_roster || []).map((u) => ({ ...u, external: true })),
  ].map((u) => ({
    id: u.id, name: u.name, external: u.external,
    role: u.role || (u.tenant_roles && u.tenant_roles[0]) || (u.roles && u.roles[0]) || (u.external ? "client" : "team"),
    open: requests.filter((r) => r.status !== "Complete" && assignedTo(r, u.id)).length,
  }));
  const groups = (() => {
    const order = cats.map((c) => c.name);
    const map = {};
    filtered.forEach((r) => { const k = r.category_name || "Uncategorized"; (map[k] = map[k] || []).push(r); });
    return Object.keys(map).sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
    }).map((k) => ({ name: k, items: map[k] }));
  })();

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!eng) return <Empty title="Engagement not found" />;

  return (
    <div>
      <button onClick={() => nav("/m1")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-to-engagements">
        <ArrowLeft className="h-4 w-4" /> Engagements
      </button>

      {isArchived && (
        <div data-testid="archived-banner" className="mb-5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400">
          <Archive className="h-4 w-4" /> This engagement is archived and read-only. All data is retained. {canManage && <button className="ml-1 font-semibold underline" data-testid="banner-unarchive" onClick={() => archiveAct("unarchive", "Engagement restored")}>Unarchive</button>}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline text-azx-collection">Engagement</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{eng.name}</h1>
          <p className="mt-1 text-sm text-slate-500">Client: {eng.client_org_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && !readOnly && (
            <Button variant="outline" data-testid="roll-forward-btn" onClick={() => setRollOpen(true)}>
              <History className="mr-2 h-4 w-4" /> Roll forward
            </Button>
          )}
          {canManage && !readOnly && (
            <>
              <Button variant="outline" data-testid="add-engagement-team-btn" onClick={() => setAddSide("internal")}><Users className="mr-2 h-4 w-4" /> Engagement team</Button>
              <Button variant="outline" data-testid="add-client-team-btn" onClick={() => setAddSide("client")}><UserPlus className="mr-2 h-4 w-4" /> Client team</Button>
            </>
          )}
          {canManage && !readOnly && <ImportExcel engId={id} onDone={load} />}
          <Button variant="outline" data-testid="master-list-btn" onClick={() => nav(`/m1/engagements/${id}/master`)}>
            <List className="mr-2 h-4 w-4" /> Master list
          </Button>
          {canManage && !readOnly && <RecycleBin engId={id} onChanged={load} />}
          {!isClient && <Button variant="outline" data-testid="received-files-btn" onClick={() => setReceivedOpen(true)}><Inbox className="mr-2 h-4 w-4" /> Items received</Button>}
          {canManage && !readOnly && <CreateRequest engId={id} phases={eng.phases} cats={cats} roster={[...(eng.internal_roster || []), ...(eng.client_roster || [])]} onDone={load} />}
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="eng-actions-menu"><MoreVertical className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isArchived
                  ? <DropdownMenuItem data-testid="eng-unarchive" onClick={() => archiveAct("unarchive", "Engagement restored")}><ArchiveRestore className="mr-2 h-4 w-4" /> Unarchive</DropdownMenuItem>
                  : <DropdownMenuItem data-testid="eng-archive" onClick={() => archiveAct("archive", "Engagement archived")}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>}
                <DropdownMenuItem data-testid="eng-delete" onClick={() => setDeleteOpen(true)} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>


      <StatTiles
        vals={{ overdue: statusCounts.Overdue, dueThisWeek: dueCounts.week, upcoming: dueCounts.next + dueCounts.later, dueCats, completeCount, total, percent }}
        isActive={(id) => id === "completed" ? activeType("complete") : activeType(id)}
        onToggle={(id) => {
          if (id === "overdue") toggleFilter("overdue", undefined, "Status: Overdue");
          else if (id === "dueThisWeek") toggleFilter("dueThisWeek", undefined, "Due: This week");
          else if (id === "upcoming") toggleFilter("upcoming", undefined, "Due: Upcoming");
          else toggleFilter("complete", undefined, "Status: Complete");
        }} />

      <PhaseCard phases={phaseProgress} overall={percent}
        isActive={(pid) => activeType("phase", pid)}
        onToggle={(pid) => { const ph = phaseProgress.find((x) => x.id === pid); toggleFilter("phase", pid, `Phase: ${ph?.name}`); }} />

      <div className="grid gap-5 lg:grid-cols-[1fr_340px] items-start">
        <RequestsPanel total={filtered.length} groups={groups}
          pills={[
            { key: "all", label: "All", active: filters.length === 0, onClick: () => setFilters([]) },
            { key: "overdue", label: "Overdue", active: activeType("overdue"), onClick: () => toggleFilter("overdue", undefined, "Status: Overdue") },
            { key: "mine", label: "Mine", active: activeType("mine"), onClick: () => toggleFilter("mine", undefined, "Assignee: Me") },
          ]}
          chips={filters.map((f) => ({ id: f.id, label: f.label }))}
          onClearAll={() => setFilters([])}
          onRemoveChip={(c) => setFilters((prev) => prev.filter((x) => x.id !== c.id))}
          canManage={canManage && !readOnly} onOpen={setOpenReq}
          onSubmitAll={canManage && !readOnly && !selecting && !showDeleted ? submitAllToClient : null}
          selecting={selecting} selectedIds={selectedIds}
          onToggleSelect={toggleSelect} onToggleCategory={toggleCategory}
          onStartSelect={() => { setSelectedIds([]); setSelecting(true); }}
          onCancelSelect={() => { setSelecting(false); setSelectedIds([]); }}
          onSelectAllVisible={selectAllVisible} allVisibleIds={filtered.map((r) => r.id)}
          onBulkStatus={(s) => runBulk({ action: "status", status: s }, (d) => `${d.affected} request(s) set to ${statusLabel(s, isClient)}`)}
          onBulkReassign={() => setReassignOpen(true)}
          onBulkCategory={(cid) => runBulk({ action: "category", category_id: cid }, (d) => `${d.affected} request(s) moved`)}
          onBulkDelete={() => setDeleteReqOpen(true)}
          onBulkRestore={() => runBulk({ action: "restore" }, (d) => `${d.affected} request(s) restored`)}
          statuses={STATUSES} cats={cats}
          deletedView={showDeleted}
          onToggleDeleted={canManage && !readOnly ? () => { setSelecting(false); setSelectedIds([]); setShowDeleted((v) => !v); } : null}
          onAdd={(catId) => { setPresetCat(catId || ""); setCreateOpen(true); }}
          onRename={() => nav("/m1/configure")} />
        <div className="space-y-5">
          <StatusChart counts={statusCounts} total={total}
            activeStatus={activeType("overdue") ? "Overdue" : filters.find((f) => f.type === "status")?.value}
            onToggle={(s) => s === "Overdue" ? toggleFilter("overdue", undefined, "Status: Overdue") : toggleFilter("status", s, `Status: ${statusLabel(s, isClient)}`)} />
          <DueChart counts={dueCounts} activeBucket={filters.find((f) => f.type === "due")?.value}
            onToggle={(b) => toggleFilter("due", b, `Due: ${b}`)} />
          <TeamCard members={members} activeAssignee={filters.find((f) => f.type === "assignee")?.value}
            onToggle={(uid) => { const m = members.find((x) => x.id === uid); toggleFilter("assignee", uid, `Assignee: ${m?.name}`); }}
            canInvite={canInvite && !readOnly} onInvite={() => setInviteOpen(true)}
            onRemove={canManage && !readOnly ? removeMember : null} />
          <ActivityCard activity={activity} onItemClick={(a) => a.request_id && setOpenReq(a.request_id)} />
        </div>
      </div>

      <RequestDialog requestId={openReq} open={!!openReq} onOpenChange={(o) => !o && setOpenReq(null)} onChanged={load} />
      <ReceivedFilesDialog engId={id} open={receivedOpen} onOpenChange={setReceivedOpen} />
      {canManage && !readOnly && <CreateRequest engId={id} phases={eng.phases} cats={cats} roster={[...(eng.internal_roster || []), ...(eng.client_roster || [])]} onDone={load} open={createOpen} onOpenChange={setCreateOpen} presetCategoryId={presetCat} />}
      <InviteClientDialog engId={id} open={inviteOpen} onOpenChange={setInviteOpen} onDone={load} />
      <AddTeamDialog engId={id} side={addSide || "internal"} open={!!addSide}
        onOpenChange={(o) => !o && setAddSide(null)}
        onDone={() => { setAddSide(null); load(); }}
        onInviteNew={() => { setAddSide(null); setInviteOpen(true); }} />
      <RollForwardDialog engagementId={id} open={rollOpen} onOpenChange={setRollOpen} onDone={load} />
      <EngagementDeleteDialog eng={deleteOpen ? eng : null} onOpenChange={(o) => !o && setDeleteOpen(false)} onDone={() => { setDeleteOpen(false); nav("/m1"); }} />
      <BulkReassignDialog open={reassignOpen} onOpenChange={setReassignOpen}
        internalUsers={eng.internal_roster || []} clientUsers={eng.client_roster || []} count={selectedIds.length}
        onApply={(ai, ac, mode) => runBulk({ action: "reassign", assigned_internal: ai, assigned_client: ac, mode }, (d) => `${d.affected} request(s) ${mode === "append" ? "updated" : "reassigned"}`)} />
      <Dialog open={deleteReqOpen} onOpenChange={setDeleteReqOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete {selectedIds.length} request(s)?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">They'll be hidden from the requests list. A Partner or Firm Admin can restore them from the Deleted view. No uploaded files are erased.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReqOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete" onClick={() => runBulk({ action: "delete" }, (d) => `${d.affected} request(s) deleted`)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BulkReassignDialog({ open, onOpenChange, internalUsers, clientUsers, count, onApply }) {
  const [internal, setInternal] = useState([]);
  const [client, setClient] = useState([]);
  const [mode, setMode] = useState("replace");
  useEffect(() => { if (open) { setInternal([]); setClient([]); setMode("replace"); } }, [open]);
  const toggle = (arr, set, uid) => set(arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Reassign {count} request(s)</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1 text-xs font-semibold" data-testid="reassign-mode">
          <button data-testid="reassign-mode-replace" onClick={() => setMode("replace")}
            className={`flex-1 rounded-md px-3 py-1.5 ${mode === "replace" ? "bg-white shadow text-indigo-600" : "text-slate-500"}`}>Replace</button>
          <button data-testid="reassign-mode-append" onClick={() => setMode("append")}
            className={`flex-1 rounded-md px-3 py-1.5 ${mode === "append" ? "bg-white shadow text-indigo-600" : "text-slate-500"}`}>Add to existing</button>
        </div>
        <p className="text-xs text-slate-500">{mode === "append"
          ? "Adds the people you tick to each selected request, keeping current assignees."
          : "Replaces the assignees on every selected request with exactly who you tick."}</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Internal assignees</Label>
            <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto" data-testid="reassign-internal">
              {internalUsers.map((u) => <label key={u.id} className="flex items-center gap-2 text-sm"><Checkbox checked={internal.includes(u.id)} onCheckedChange={() => toggle(internal, setInternal, u.id)} />{u.name}</label>)}
            </div>
          </div>
          <div>
            <Label className="text-xs">Client assignees</Label>
            <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto" data-testid="reassign-client">
              {clientUsers.length === 0 && <span className="text-xs text-slate-400">No client users</span>}
              {clientUsers.map((u) => <label key={u.id} className="flex items-center gap-2 text-sm"><Checkbox checked={client.includes(u.id)} onCheckedChange={() => toggle(client, setClient, u.id)} />{u.name}</label>)}
            </div>
          </div>
        </div>
        <DialogFooter><Button data-testid="apply-reassign" onClick={() => onApply(internal, client, mode)}>Apply</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteClientDialog({ engId, open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", role: "client_user" });
  const invite = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      await api.post("/m1/client-invite", { engagement_id: engId, ...form });
      toast.success("Client invited (temp password: Demo@123)");
      onOpenChange(false); setForm({ name: "", email: "", role: "client_user" }); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Invite client user</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input data-testid="invite-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="invite-email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="client_user">Client User</SelectItem><SelectItem value="client_admin">Client Admin</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={invite} data-testid="invite-send">Send invite</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceivedFilesDialog({ engId, open, onOpenChange }) {
  const [files, setFiles] = useState([]);
  const [engName, setEngName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    if (!open) return;
    setLoading(true); setSel([]);
    api.get(`/m1/engagements/${engId}/received-files`)
      .then((r) => { setFiles(r.data.files || []); setEngName(r.data.engagement_name || ""); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, engId]);
  const allIds = files.map((f) => f.id);
  const toggle = (fid) => setSel((p) => p.includes(fid) ? p.filter((x) => x !== fid) : [...p, fid]);
  const toggleAll = () => setSel((p) => allIds.length && allIds.every((i) => p.includes(i)) ? [] : allIds);
  const downloadOne = async (f) => {
    try {
      const { data } = await api.get(`/m1/attachments/${f.id}/download`);
      if (data.url) { window.open(data.url, "_blank", "noopener"); return; }
      const link = document.createElement("a");
      link.href = `data:${data.content_type};base64,${data.content_b64}`;
      link.download = data.filename; link.click();
    } catch (e) { toast.error(apiError(e)); }
  };
  const downloadZip = async (ids) => {
    try {
      const res = await api.post("/m1/attachments/download-zip", { attachment_ids: ids }, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a"); link.href = url; link.download = "received-files.zip"; link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh] overflow-y-auto" data-testid="received-files-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Inbox className="h-5 w-5 text-indigo-600" /> Items received{engName ? ` — ${engName}` : ""}</DialogTitle>
          <DialogDescription>All files uploaded by the client across this engagement.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : files.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400" data-testid="received-empty">No client files uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500">
                <Checkbox checked={sel.length > 0 && allIds.every((i) => sel.includes(i))} onCheckedChange={toggleAll} data-testid="received-select-all" />
                {sel.length ? `${sel.length} selected` : `Select all (${files.length})`}
              </label>
              <Button size="sm" variant="outline" disabled={!sel.length} data-testid="received-download-zip" onClick={() => downloadZip(sel)}>
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download ZIP
              </Button>
            </div>
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid={`received-file-${f.id}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <Checkbox checked={sel.includes(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`received-select-${f.id}`} />
                  <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{f.filename}</div>
                    <div className="truncate text-xs text-slate-400">{f.request_name} · {f.uploaded_by_name} · {((f.size || 0) / 1024).toFixed(1)} KB</div>
                  </div>
                </div>
                <Button size="icon" variant="ghost" data-testid={`received-download-${f.id}`} onClick={() => downloadOne(f)}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateRequest({ engId, phases, cats, roster, onDone, open: openProp, onOpenChange, presetCategoryId }) {
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = controlled ? openProp : openState;
  const setOpen = controlled ? onOpenChange : setOpenState;
  const [f, setF] = useState({ name: "", description: "", due_date: "", phase_id: "", category_id: "", file_visibility: "standard", restricted: false });
  const [internal, setInternal] = useState([]);
  const [client, setClient] = useState([]);
  const internalUsers = roster.filter((u) => !u.external);
  const clientUsers = roster.filter((u) => u.external);

  useEffect(() => { if (open && presetCategoryId) setF((prev) => ({ ...prev, category_id: presetCategoryId })); }, [open, presetCategoryId]);

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      await api.post("/m1/requests", {
        engagement_id: engId, ...f, phase_id: f.phase_id || null, category_id: f.category_id || null,
        assigned_internal: internal, assigned_client: client, allowed_users: f.restricted ? [...internal, ...client] : [],
      });
      toast.success("Request created");
      setOpen(false); setF({ name: "", description: "", due_date: "", phase_id: "", category_id: "", file_visibility: "standard", restricted: false });
      setInternal([]); setClient([]); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && <DialogTrigger asChild><Button data-testid="create-request-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" /> New request</Button></DialogTrigger>}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create request</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input data-testid="req-name" className="mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Description</Label><Textarea data-testid="req-desc" className="mt-1.5" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Due date</Label><Input type="date" data-testid="req-due" className="mt-1.5" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
            <div>
              <Label>Category</Label>
              <CategoryCombobox cats={cats} value={f.category_id}
                onChange={(v) => setF({ ...f, category_id: v })} onCreated={onDone} />
            </div>
          </div>
          <div>
            <Label>Phase</Label>
            <Select value={f.phase_id} onValueChange={(v) => setF({ ...f, phase_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="req-phase"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{phases?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Internal assignees</Label>
              <div className="mt-1.5 space-y-1 max-h-28 overflow-y-auto">
                {internalUsers.map((u) => <label key={u.id} className="flex items-center gap-2 text-sm"><Checkbox checked={internal.includes(u.id)} onCheckedChange={() => toggle(internal, setInternal, u.id)} />{u.name}</label>)}
              </div>
            </div>
            <div>
              <Label className="text-xs">Client assignees</Label>
              <div className="mt-1.5 space-y-1 max-h-28 overflow-y-auto">
                {clientUsers.length === 0 && <span className="text-xs text-slate-400">Invite clients first</span>}
                {clientUsers.map((u) => <label key={u.id} className="flex items-center gap-2 text-sm"><Checkbox checked={client.includes(u.id)} onCheckedChange={() => toggle(client, setClient, u.id)} />{u.name}</label>)}
              </div>
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm" data-testid="req-restricted-label"><Checkbox checked={f.restricted} onCheckedChange={(v) => setF({ ...f, restricted: v })} data-testid="req-restricted" /> Restrict to assignees only (granular override)</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.file_visibility === "hidden_until_submit"} onCheckedChange={(v) => setF({ ...f, file_visibility: v ? "hidden_until_submit" : "standard" })} data-testid="req-hidden-files" /> Hide files from internal team until client submits</label>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="req-save" className="bg-indigo-600 hover:bg-indigo-700">Create request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
